import { AspectRatio, Button, Flex, Loader, Text } from '@mantine/core';
import { generateGetSignedUrl } from 'api/action';
import { useGetReceiptByInvoice } from 'api/resource/receipt';
import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';

interface ClaimReceiptsProps {
  invoiceID: string;
}
const RECEIPT_BUCKET = `invoice-files-${import.meta.env.VITE_ACCOUNT_ID}`;

export const ViewReceipts: React.FC<ClaimReceiptsProps> = ({ invoiceID }) => {
  const {
    data: receiptData,
    isLoading,
    isError,
    error,
  } = useGetReceiptByInvoice(invoiceID);

  const [receiptKey, setReceiptKey] = useState<string>('');
  const [url, setUrl] = useState<string>('');

  useEffect(() => {
    if (!receiptData) return;
    const key = encodeURI(
      `receipts/invoice/${invoiceID}/${receiptData.id}.pdf`
    );
    setReceiptKey(key);
  }, [receiptData, invoiceID]);

  useEffect(() => {
    const fetchUrl = async () => {
      try {
        const signed = await generateGetSignedUrl({
          bucket: RECEIPT_BUCKET,
          key: receiptKey,
        });
        setUrl(signed);
      } catch (err) {
        Sentry.captureException(err, {
          tags: { component: 'ViewReceipts' },
          extra: { bucket: RECEIPT_BUCKET, key: receiptKey },
        });
      }
    };
    if (receiptKey !== '') fetchUrl();
  }, [receiptKey]);

  if (isLoading) return <Loader />;
  if (isError)
    return (
      <Text size="sm">{error?.message || 'Failed to load receipts.'}</Text>
    );

  if (!receiptData)
    return <Text size="sm">No receipts found for this invoice.</Text>;

  return (
    <Flex direction={'column'} gap={'sm'}>
      {receiptData && (
        <>
          <AspectRatio ratio={8.5 / 11} style={{ width: '100%' }}>
            <iframe
              src={`${url}#toolbar=0&navpanes=0&scrollbar=0`}
              style={{ border: 'none', flex: 1 }}
            />
          </AspectRatio>
          <Flex justify="flex-end" mt="sm">
            <Button
              component="a"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              download={`receipt-${receiptData.id}.pdf`}
              variant="outline"
            >
              Download PDF
            </Button>
          </Flex>
        </>
      )}
    </Flex>
  );
};
